import api from "../../../core/api";
import { Page } from "../../../shared/models/models";
import {
  Offer,
  OffersQuery,
  OffersFilters,
  OffersFiltersQuery,
  CreateOffer,
  ChangeOffer,
  OfferStatusCode,
} from "../models/models";

const getRequestConfig = () => {
  return { baseURL: (window as any).app.config.servicingApiBaseUrl };
};

const serializeParams = (params: any): string => {
  const parts: string[] = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      value.forEach((item) => {
        parts.push(`${key}=${encodeURIComponent(item)}`);
      });
    } else if (value !== undefined && value !== null) {
      parts.push(`${key}=${encodeURIComponent(value)}`);
    }
  });
  return parts.join("&");
};

class OffersApi {
  getOffersFilters(query: OffersFiltersQuery): Promise<OffersFilters> {
    return api
      .get<OffersFilters>(`/v1/customer/offers/filters`, {
        ...getRequestConfig(),
        params: query,
        paramsSerializer: serializeParams,
      })
      .then((x) => x.data);
  }

  getOffers(query: OffersQuery): Promise<Page<Offer>> {
    return api
      .get<Page<Offer>>(`/v1/customer/offers`, {
        ...getRequestConfig(),
        params: query,
        paramsSerializer: serializeParams,
      })
      .then((x) => x.data);
  }

  getOffer(id: string): Promise<Offer> {
    return api
      .get<Offer>(`/v1/customer/offers/${id}`, getRequestConfig())
      .then((x) => x.data);
  }

  createOffer(model: CreateOffer): Promise<Offer> {
    return api
      .post<Offer>(`/v1/customer/offers`, model, getRequestConfig())
      .then((x) => x.data);
  }

  changeOffer(id: string, model: ChangeOffer): Promise<any> {
    return api
      .put<void>(`/v1/customer/offers/${id}`, model, getRequestConfig())
      .then((x) => x.data);
  }

  submitOffer(id: string): Promise<any> {
    return api
      .post<void>(`/v1/customer/offers/${id}/submit`, null, getRequestConfig())
      .then((x) => x.data);
  }

  withdrawOffer(id: string): Promise<any> {
    return api
      .post<void>(
        `/v1/customer/offers/${id}/withdraw`,
        null,
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  deleteOffer(id: string): Promise<any> {
    return api
      .delete<void>(`/v1/customer/offers/${id}`, getRequestConfig())
      .then((x) => x.data);
  }

  archiveOffer(id: string): Promise<any> {
    return api
      .post<void>(`/v1/customer/offers/${id}/archive`, null, getRequestConfig())
      .then((x) => x.data);
  }

  unarchiveOffer(id: string): Promise<any> {
    return api
      .post<void>(
        `/v1/customer/offers/${id}/unarchive`,
        null,
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  async getExistingOfferForSR(serviceRequestId: string): Promise<Offer | null> {
    const activeStatuses = [
      OfferStatusCode.draft,
      OfferStatusCode.sent,
      OfferStatusCode.viewed,
      OfferStatusCode.negotiating,
      OfferStatusCode.accepted,
      OfferStatusCode.awaitingConfirmation,
      OfferStatusCode.confirmed,
      OfferStatusCode.workInProgress,
    ];
    const result = await this.getOffers({
      pageNo: 1,
      pageSize: 100,
      statusCodes: activeStatuses,
    });
    return (
      result.items.find(
        (o) => o.serviceRequest?.id === serviceRequestId
      ) || null
    );
  }

  confirmOffer(
    id: string,
    data: {
      arrivalDate: string;
      arrivalTimeFrom: string;
      arrivalTimeTo: string;
      message?: string;
    }
  ): Promise<any> {
    return api
      .post(`/v1/customer/offers/${id}/confirm`, data, getRequestConfig())
      .then((x) => x.data);
  }

  async downloadCalendar(offerId: string): Promise<void> {
    const response = await api.get(
      `/v1/customer/offers/${offerId}/calendar`,
      {
        ...getRequestConfig(),
        responseType: "blob",
      }
    );

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers["content-disposition"];
    let filename = "service-appointment.ics";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "");
      }
    }

    // Create a blob URL and trigger download
    const blob = new Blob([response.data], { type: "text/calendar" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export default new OffersApi();
